import { useEffect, useMemo, useState } from 'react';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import SatelliteAltRoundedIcon from '@mui/icons-material/SatelliteAltRounded';
import StreetviewRoundedIcon from '@mui/icons-material/Streetview';
import ViewInArRoundedIcon from '@mui/icons-material/ViewInArRounded';
import { Box, Tab, Tabs } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { GEMINI_GRADIENT } from '../../theme';
import { EmployerFootprintMap } from './EmployerFootprintMap';
import { EmployerSiteAerialStage } from './EmployerSiteAerialStage';
import { EmployerSite3DStage } from './EmployerSite3DStage';
import { EmployerSiteStreetStage } from './EmployerSiteStreetStage';
import { useEmployerSiteViewpoints } from './useEmployerSiteViewpoints';
import { SITE_COVERAGE_RADIUS_M } from '../../lib/employerSiteViewpoints';
import { EMPLOYER_STREET_VIEW_TOUR_TARGET } from '../../lib/employerReview/checkpointTourCopy';
import { isStreetViewTourStep } from '../../lib/employerReview/buildFindingEvidenceTourSteps';
import { StreetViewTourGuide } from '../employerReview/guidedTour/StreetViewTourGuide';
import { useEmployerEvidenceTourOptional } from '../employerReview/guidedTour/EmployerEvidenceTourContext';

type ViewTab = 'map' | 'satellite' | 'street' | 'building';

type EmployerPin = {
  name: string;
  address: string | null;
  latitude: number;
  longitude: number;
  distanceKm: number | null;
};

type Props = {
  purchases: Array<{
    merchantName: string;
    address: string | null;
    latitude: number;
    longitude: number;
    category: string | null;
  }>;
  employer: EmployerPin | null;
  /** Fixed pixel height when not filling the parent column. */
  height?: number;
  /** Stretch map / street view to the full height of the footprint column. */
  fillHeight?: boolean;
};

function ViewLayer({ visible, children }: { visible: boolean; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        visibility: visible ? 'visible' : 'hidden',
        zIndex: visible ? 1 : 0,
      }}
      aria-hidden={!visible}
    >
      {children}
    </Box>
  );
}

/**
 * Footprint map plus inline Satellite / Street view / 3D tabs. Street View
 * replaces this same frame — no Around-the-building rail.
 */
export function EmployerFootprintViewFrame({
  purchases,
  employer,
  height = 320,
  fillHeight = false,
}: Props) {
  const tour = useEmployerEvidenceTourOptional();
  const activeStep = tour?.activeTour?.steps[tour.activeTour.stepIndex];
  const streetGuideActive = Boolean(activeStep && isStreetViewTourStep(activeStep));

  const [tab, setTab] = useState<ViewTab>('map');
  const [visited, setVisited] = useState<ViewTab[]>(['map']);

  const building = useMemo(
    () => (employer ? { lat: employer.latitude, lng: employer.longitude } : null),
    [employer],
  );

  const site = useEmployerSiteViewpoints({
    building,
    enabled: Boolean(building) && (tab === 'street' || tab === 'satellite' || tab === 'building'),
  });
  const nearestViewpoint = site.viewpoints[0] ?? null;

  const selectTab = (next: ViewTab) => {
    setTab(next);
    setVisited((prev) => (prev.includes(next) ? prev : [...prev, next]));
  };

  useEffect(() => {
    if (!streetGuideActive) return;
    setTab('street');
    setVisited((prev) => (prev.includes('street') ? prev : [...prev, 'street']));
  }, [streetGuideActive]);

  const viewportHeight = fillHeight ? '100%' : height;

  return (
    <Box
      sx={
        fillHeight
          ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }
          : undefined
      }
    >
      {employer ? (
        <Tabs
          value={tab}
          onChange={(_, next: ViewTab) => selectTab(next)}
          variant="scrollable"
          allowScrollButtonsMobile
          data-testid="employer-footprint-view-tabs"
          sx={{
            minHeight: 34,
            mb: 1,
            px: 0.45,
            py: 0.4,
            width: 'fit-content',
            maxWidth: '100%',
            borderRadius: 2,
            bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.06 : 0.16),
            border: '1px solid',
            borderColor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.18 : 0.34),
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': { gap: 0.4 },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.78rem',
              minHeight: 30,
              minWidth: 0,
              px: 1.2,
              py: 0.4,
              borderRadius: 1.5,
              color: 'text.secondary',
              transition: 'background-color 160ms ease, color 160ms ease, box-shadow 160ms ease',
              '& .MuiTab-iconWrapper': { mb: '0 !important', mr: 0.6 },
              '&:hover': {
                color: 'primary.main',
                bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.1 : 0.22),
              },
              '&.Mui-selected': {
                color: '#fff',
                background: GEMINI_GRADIENT,
                boxShadow: (t) => `0 2px 8px ${alpha(t.palette.primary.main, 0.38)}`,
              },
            },
          }}
        >
          <Tab value="map" icon={<MapRoundedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Map" />
          <Tab
            value="satellite"
            icon={<SatelliteAltRoundedIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label="Satellite"
          />
          <Tab
            value="street"
            icon={<StreetviewRoundedIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label="Street view"
          />
          <Tab
            value="building"
            icon={<ViewInArRoundedIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label="3D building"
          />
        </Tabs>
      ) : null}

      {employer ? (
        <Box
          sx={{
            position: 'relative',
            ...(fillHeight
              ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
              : {}),
          }}
        >
          <Box
            sx={{
              visibility: tab === 'map' ? 'visible' : 'hidden',
              position: tab === 'map' ? 'relative' : 'absolute',
              width: '100%',
              ...(fillHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
              pointerEvents: tab === 'map' ? 'auto' : 'none',
            }}
            aria-hidden={tab !== 'map'}
          >
            <EmployerFootprintMap
              purchases={purchases}
              employer={employer}
              height={fillHeight ? '100%' : height}
              active={tab === 'map'}
            />
          </Box>

          {building ? (
            <Box
              sx={{
                visibility: tab === 'map' ? 'hidden' : 'visible',
                position: tab === 'map' ? 'absolute' : 'relative',
                width: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: streetGuideActive
                  ? (t) => alpha('#0f766e', t.palette.mode === 'light' ? 0.28 : 0.38)
                  : 'divider',
                bgcolor: 'background.paper',
                pointerEvents: tab === 'map' ? 'none' : 'auto',
                ...(fillHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
              }}
              aria-hidden={tab === 'map'}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: viewportHeight,
                  width: '100%',
                  ...(fillHeight ? { flex: 1, minHeight: 280 } : {}),
                }}
              >
                {visited.includes('satellite') ? (
                  <ViewLayer visible={tab === 'satellite'}>
                    <EmployerSiteAerialStage
                      building={building}
                      employerName={employer.name}
                      viewpoints={[]}
                      activePanoId={null}
                      onSelectViewpoint={() => undefined}
                    />
                  </ViewLayer>
                ) : null}

                {visited.includes('street') ? (
                  <ViewLayer visible={tab === 'street'}>
                    <Box
                      data-tour-target={EMPLOYER_STREET_VIEW_TOUR_TARGET}
                      sx={{ position: 'relative', height: '100%', width: '100%' }}
                    >
                      <EmployerSiteStreetStage
                        building={building}
                        employerName={employer.name}
                        viewpoint={nearestViewpoint}
                        status={site.status}
                        coverageRadiusM={SITE_COVERAGE_RADIUS_M}
                        lookupError={site.error}
                        slowRotate={streetGuideActive}
                      />
                    </Box>
                  </ViewLayer>
                ) : null}

                {visited.includes('building') ? (
                  <ViewLayer visible={tab === 'building'}>
                    <EmployerSite3DStage
                      building={building}
                      employerName={employer.name}
                      active={tab === 'building'}
                      onUseAerialInstead={() => selectTab('satellite')}
                    />
                  </ViewLayer>
                ) : null}
              </Box>

              {streetGuideActive ? <StreetViewTourGuide /> : null}
            </Box>
          ) : null}
        </Box>
      ) : (
        <EmployerFootprintMap
          purchases={purchases}
          employer={employer}
          height={fillHeight ? '100%' : height}
          active
        />
      )}
    </Box>
  );
}
